import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { postMessage, VoxSource, VoxEventType } from '../events';

declare global {
  interface Window {
    Argyle?: {
      create: (config: ArgyleConfig) => ArgyleLinkInstance;
    };
  }
}

interface ArgyleLinkInstance {
  open: () => void;
  close: () => void;
}

interface ArgyleConfig {
  userToken: string;
  sandbox: boolean;
  flowId?: string;
  ddsConfig?: string;
  items?: string[];
  accountId?: string;
  language?: string;
  onAccountConnected?: (payload: any) => void;
  onAccountCreated?: (payload: any) => void;
  onAccountError?: (payload: any) => void;
  onAccountRemoved?: (payload: any) => void;
  onDDSError?: (payload: any) => void;
  onDDSSuccess?: (payload: any) => void;
  onCantFindItemClicked?: (payload: any) => void;
  onClose?: (payload: any) => void;
  onDocumentsSubmitted?: (payload: any) => void;
  onFormSubmitted?: (payload: any) => void;
  onUIEvent?: (payload: any) => void;
  onError?: (payload: any) => void;
  onTokenExpired?: (updateToken: (newToken: string) => void) => void;
}

export const Argyle = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const linkInstanceRef = useRef<ArgyleLinkInstance | null>(null);

  // Decode and parse token
  let parsedToken: Partial<ArgyleConfig> | null = null;
  try {
    parsedToken = JSON.parse(atob(token));
  } catch (e) {
    console.error('Error decoding token:', e);
    postMessage({ eventType: VoxEventType.ERROR, payload: { error: e }, _metadata: { source: VoxSource.ARGYLE, eventType: 'onError' } });
  }

  useEffect(() => {
    if (!parsedToken) return;

    // Initialize Argyle after script loads
    (async () => {
      try {
        // Load the Argyle script if not already loaded
        if (!window.Argyle) {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://plugin.argyle.com/argyle.web.v5.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (window.Argyle && parsedToken) {
          const token = parsedToken as any;
          linkInstanceRef.current = window.Argyle.create({
            ...parsedToken,
            // TODO: REMOVE THIS AFTER TESTING
            userToken: token.accessToken || token.userToken || '',
            flowId: token.argyleFlowId || token.flowId || null,
            sandbox: token.isProduction !== undefined ? !token.isProduction : (token.sandbox ?? false),
            // Account callbacks
            onAccountConnected: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onAccountConnected' } });
            },
            onAccountCreated: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onAccountCreated' } });
            },
            onAccountError: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onAccountError' } });
            },
            onAccountRemoved: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onAccountRemoved' } });
            },
            // Deposit switching callbacks
            onDDSError: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onDDSError' } });
            },
            onDDSSuccess: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onDDSSuccess' } });
            },
            // User flow callbacks
            onCantFindItemClicked: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onCantFindItemClicked' } });
            },
            onClose: (payload) => {
              postMessage({ eventType: VoxEventType.COMPLETE, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onClose' } });
            },
            onDocumentsSubmitted: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onDocumentsSubmitted' } });
            },
            onFormSubmitted: (payload) => {
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onFormSubmitted' } });
            },
            onUIEvent: (payload) => {
              if (payload.name === "search - opened") return postMessage({ eventType: VoxEventType.LOAD, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onUIEvent' } });
              if (payload.name === "search - link item selected") return postMessage({ eventType: VoxEventType.EMPLOYER_SELECTED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onUIEvent' } });
              postMessage({ eventType: VoxEventType.UNDEFINED, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onUIEvent' } });
            },
            // Operational callbacks
            onError: (payload) => {
              postMessage({ eventType: VoxEventType.ERROR, payload, _metadata: { source: VoxSource.ARGYLE, eventType: 'onError' } });
            },
            onTokenExpired: (updateToken) => {
              postMessage({ eventType: VoxEventType.TOKEN_EXPIRED, payload: { updateToken}, _metadata: { source: VoxSource.ARGYLE, eventType: 'onTokenExpired' } });
              // Generate a new user token.
              // updateToken(newToken)
            },
          } as ArgyleConfig);
          linkInstanceRef.current.open();
        }
      } catch (error) {
        console.error('Failed to load Argyle script:', error);
        postMessage({ eventType: VoxEventType.ERROR, payload: { error }, _metadata: { source: VoxSource.ARGYLE, eventType: 'onError' } });
      }
    })();

    // Cleanup function
    return () => {
      if (linkInstanceRef.current) {
        // Optionally close the link instance on unmount
        // linkInstanceRef.current.close()
      }
    };
  }, [parsedToken]);

  return <div />;
};
